<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';

/**
 * アップロードパネルが発火するイベント
 */
type Emits = {
  /** ファイルが選択された */
  (event: 'file-selected', file: File): void;
  /** 選択済みファイルを破棄する */
  (event: 'cancel'): void;
  /** 書式読み込みアクションを実行する */
  (event: 'submit', file: File): void;
  /** 許可されていないファイルが選択された */
  (event: 'invalid-file', reason: string): void;
};

/**
 * アップロードパネルの入力パラメータ
 */
type Props = {
  /** 書式読込ボタンのロード状態 */
  loading?: boolean;
  /** パネル状態の外部リセットトリガー */
  resetKey?: number;
  /** 選択可能な拡張子 */
  acceptExtensions?: string[];
  /** ドロップゾーン上部に表示する説明 */
  introMessage?: string;
};

const t = i18n.t;
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  resetKey: 0,
  /**
   *　　許可される拡張子のデフォルト値
   *　　- Excelファイル（.xlsx）のみ許可
   */
  acceptExtensions: () => ['.xlsx'],
  introMessage: '',
});

const emits = defineEmits<Emits>();

const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const errorMessage = ref('');

/**
 * ファイル入力のaccept属性値を返す
 * @returns - accept属性値
 */
const acceptAttribute = computed(() => props.acceptExtensions.join(','));

/**
 * ファイルサイズ表示を計算する
 */
const formattedFileSize = computed(() => {
  if (!selectedFile.value) {
    return '';
  }
  const size = selectedFile.value.size;
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
});

/**
 * ドラッグ関連イベントの既定動作を抑止する
 * @param event - ブラウザイベント
 */
const preventDefaults = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * ドラッグ状態の開始を処理する
 * @param event - ブラウザイベント
 */
const handleDragEnter = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = true;
};

/**
 * ドラッグ状態の終了を処理する
 * @param event - ブラウザイベント
 */
const handleDragLeave = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = false;
};

/**
 * ドラッグ＆ドロップされたファイルを処理する
 * @param event - ブラウザイベント
 */
const handleDrop = (event: DragEvent) => {
  preventDefaults(event);
  isDragging.value = false;
  const droppedFiles = event.dataTransfer?.files;
  if (!droppedFiles || droppedFiles.length === 0) {
    return;
  }
  handleFiles(droppedFiles[0]);
};

/**
 * ファイル選択ボタン経由で選択されたファイルを処理する
 * @param event - ブラウザイベント
 */
const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  const file = target?.files?.[0];
  if (file) {
    handleFiles(file);
  }
};

/**
 * 選択されたファイルを検証する
 * @param file - ユーザーが指定したファイル
 */
const handleFiles = (file: File) => {
  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (!props.acceptExtensions.includes(extension)) {
    const message = t('対応していない拡張子です') as string;
    errorMessage.value = message;
    emits('invalid-file', message);
    return;
  }
  errorMessage.value = '';
  selectedFile.value = file;
  emits('file-selected', file);
};

/**
 * ファイル選択をキャンセルする
 */
const handleCancel = () => {
  selectedFile.value = null;
  errorMessage.value = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
  emits('cancel');
};

/**
 * ファイル送信を実行する
 */
const handleSubmit = () => {
  if (!selectedFile.value || props.loading) {
    return;
  }
  emits('submit', selectedFile.value);
};

watch(
  () => props.resetKey,
  () => {
    handleCancel();
  }
);

onMounted(() => {
  window.addEventListener('dragover', preventDefaults);
  window.addEventListener('drop', preventDefaults);
});

onBeforeUnmount(() => {
  window.removeEventListener('dragover', preventDefaults);
  window.removeEventListener('drop', preventDefaults);
});
</script>

<template>
  <div class="upload-panel">
    <p v-if="props.introMessage" class="upload-panel__intro">
      {{ props.introMessage }}
    </p>
    <div
      class="upload-panel__drop-zone"
      :class="{ 'upload-panel__drop-zone--active': isDragging }"
      @dragenter="handleDragEnter"
      @dragover="handleDragEnter"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <Mdi class="upload-panel__icon" icon="file_download" :aria-label="t('ファイルを読み込み')" />
      <p class="upload-panel__message upload-panel__message--primary">
        {{ t('ここにファイルをドロップ') }}
      </p>
      <p class="upload-panel__divider">{{ t('または') }}</p>
      <label class="upload-panel__file-select">
        <span class="upload-panel__file-select-text">{{ t('ファイルを選択') }}</span>
        <input ref="fileInput" class="upload-panel__input" type="file" :accept="acceptAttribute" @change="handleChange" />
      </label>
      <p class="upload-panel__hint">{{ t('対応形式') }}: {{ props.acceptExtensions.join(', ') }}</p>
    </div>

    <div v-if="selectedFile" class="upload-panel__file-info">
      <span class="upload-panel__file-name">{{ selectedFile.name }}</span>
      <span class="upload-panel__file-size">{{ formattedFileSize }}</span>
    </div>
    <p v-else class="upload-panel__placeholder">
      {{ t('ファイルが選択されていません') }}
    </p>
    <p v-if="errorMessage" class="upload-panel__error">{{ errorMessage }}</p>

    <div class="upload-panel__actions">
      <button class="upload-panel__button upload-panel__button--primary" :disabled="!selectedFile || loading" @click="handleSubmit">
        {{ t('書式読み込み') }}
      </button>
      <button class="upload-panel__button upload-panel__button--secondary" :disabled="!selectedFile || loading" @click="handleCancel">
        {{ t('キャンセル') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-panel {
  display: grid;
  gap: 16px;
  padding: 24px;
  border-radius: 4px;
  border: 1px solid $borderDefault;
  background-color: $semanticWhite;

  &__intro {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    font-family: 'Noto Sans JP', sans-serif;
    color: $textDefault;
  }

  &__drop-zone {
    display: grid;
    gap: 16px;
    justify-items: center;
    padding: 48px 16px;
    border: 4px dashed $borderDefault;
    border-radius: 4px;
    background-color: $backgroundDefault;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &--active {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }
  }

  &__icon {
    font-size: 60px;
    color: $semanticPrimary;
    width: 60px;
    height: 60px;
  }

  &__message {
    margin: 0;
    font-size: 16px;
    color: $textSecondary;
    font-family: 'Noto Sans JP', sans-serif;

    &--primary {
      font-size: 22px;
      font-weight: 700;
      line-height: 1.3;
      color: $textSecondary;
      text-align: center;
    }
  }

  &__divider {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    font-family: 'Noto Sans JP', sans-serif;
    color: $textDisable;
  }

  &__file-select {
    cursor: pointer;
    display: grid;
    justify-items: center;
    text-align: center;
  }

  &__file-select-text {
    font-size: 18px;
    font-weight: 400;
    line-height: 1;
    font-family: 'Noto Sans JP', sans-serif;
    color: $semanticPrimary;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
  }

  &__input {
    display: none;
  }

  &__hint {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    font-family: 'Noto Sans JP', sans-serif;
    color: $textDisable;
  }

  &__file-info {
    display: grid;
    grid-template-columns: auto max-content;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 4px;
    background-color: $surfaceDefault;
    color: $textDefault;
  }

  &__file-name {
    font-weight: 600;
  }

  &__file-size {
    font-size: 12px;
    color: $textSecondary;
  }

  &__placeholder {
    margin: 0;
    font-size: 14px;
    color: $textSecondary;
  }

  &__error {
    margin: 0;
    font-size: 14px;
    color: $semanticError;
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 160px));
    gap: 16px;
    justify-content: center;
    justify-items: center;
  }

  &__button {
    width: 100%;
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid transparent;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--primary {
      background-color: $semanticPrimary;
      color: $semanticWhite;
    }

    &--secondary {
      background-color: $semanticWhite;
      color: $textDefault;
      border-color: $borderDefault;
    }
  }
}
</style>
